import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api/client';

// ─── Notification Toast ──────────────────────
function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  const colors = {
    success: 'bg-green-500/10 border-green-500/30 text-green-400',
    error: 'bg-red-500/10 border-red-500/30 text-red-400',
    info: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
  };

  return (
    <div className={`fixed top-20 right-4 z-50 border rounded-lg px-4 py-3 shadow-xl ${colors[type]} animate-fade-in`}>
      {message}
    </div>
  );
}

// ─── Stat Card ──────────────────────
function StatCard({ label, value, sub, icon }) {
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-slate-400 text-sm font-medium">{label}</span>
        <span className="text-2xl">{icon}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {sub && <div className="text-sm text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

// ─── Usage Chart ──────────────────────
function UsageChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Daily Usage</h3>
        <div className="text-slate-500 text-center py-8">No usage data yet. Make your first API call!</div>
      </div>
    );
  }

  const maxCalls = Math.max(...data.map(d => d.calls), 1);

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
      <h3 className="text-lg font-semibold text-white mb-4">Daily Usage (Last 30 Days)</h3>
      <div className="flex items-end gap-1 h-40">
        {data.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center justify-end group relative">
            <div className="absolute -top-8 hidden group-hover:block bg-slate-700 text-xs text-white px-2 py-1 rounded whitespace-nowrap">
              {d.date}: {d.calls} calls
            </div>
            <div
              className="w-full bg-emerald-500/80 hover:bg-emerald-500 rounded-t transition-all"
              style={{ height: `${(d.calls / maxCalls) * 100}%`, minHeight: d.calls > 0 ? '4px' : '0' }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Dashboard ──────────────────────
function DashboardPage() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview');
  const [usage, setUsage] = useState(null);
  const [keys, setKeys] = useState([]);

  const [paymentMethod, setPaymentMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // API Key creation state
  const [newKey, setNewKey] = useState(null);
  const [keyName, setKeyName] = useState('');
  const [creatingKey, setCreatingKey] = useState(false);

  // Payment form state
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [usageRes, keysRes, pmRes] = await Promise.all([
        api.billing.getUsage(),
        api.billing.getKeys(),
        api.billing.getPaymentMethod(),
      ]);
      setUsage(usageRes.data);
      setKeys(keysRes.data);
      setPaymentMethod(pmRes.data);
    } catch (e) {
      console.error('Dashboard load error:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isAuthenticated) {
      loadData();
    }
  }, [authLoading, isAuthenticated, navigate, loadData]);

  // ─── API Key Actions ──────────────────────
  const createKey = async () => {
    setCreatingKey(true);
    try {
      const res = await api.billing.createKey(keyName || 'Default');
      setNewKey(res.data.key);
      setKeys(prev => [res.data, ...prev]);
      setKeyName('');
      setUsage(prev => prev ? { ...prev, api_key_count: (prev.api_key_count || 0) + 1 } : prev);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to create key.';
      showToast(msg, 'error');
    }
    setCreatingKey(false);
  };

  const revokeKey = async (id) => {
    try {
      await api.billing.revokeKey(id);
      setKeys(prev => prev.map(k => k.id === id ? { ...k, is_active: false } : k));
      setUsage(prev => prev ? { ...prev, api_key_count: Math.max(0, (prev.api_key_count || 1) - 1) } : prev);
      showToast('API key revoked.');
    } catch (err) {
      showToast('Failed to revoke key.', 'error');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showToast('Copied to clipboard!', 'info');
    });
  };

  // ─── Loading State ──────────────────────
  if (authLoading || loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <div className="pr-spinner" />
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'keys', label: 'API Keys' },
    { id: 'playground', label: 'Playground' },
    { id: 'billing', label: 'Billing' },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Helmet>
        <title>Dashboard — CommunityGuardAPI</title>
        <meta name="description" content="Manage your API keys, view usage, and configure billing." />
        <meta property="og:title" content="Dashboard — CommunityGuardAPI" />
        <meta property="og:description" content="Manage your API keys, view usage, and configure billing." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://communityguardapi.com/dashboard" />
        <meta name="twitter:card" content="summary" />
      </Helmet>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400 mt-1">Welcome back, {user?.name || user?.email}</p>
        </div>
        <div className="mt-4 sm:mt-0 flex gap-3">
          <Link
            to="/api-docs"
            className="px-4 py-2 border border-slate-600 text-slate-300 hover:text-white hover:border-slate-500 rounded-lg text-sm font-medium transition"
          >
            API Docs
          </Link>
          <button
            onClick={() => { setActiveTab('keys'); }}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-500/80 text-white rounded-lg text-sm font-medium transition shadow"
          >
            + New API Key
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-700 mb-8">
        <nav className="flex gap-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium transition border-b-2 ${
                activeTab === tab.id
                  ? 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab usage={usage} />
      )}
      {activeTab === 'keys' && (
        <KeysTab
          keys={keys}
          newKey={newKey}
          keyName={keyName}
          setKeyName={setKeyName}
          creatingKey={creatingKey}
          createKey={createKey}
          revokeKey={revokeKey}
          copyToClipboard={copyToClipboard}
          setNewKey={setNewKey}
        />
      )}
      {activeTab === 'playground' && (
        <PlaygroundTab keys={keys} />
      )}
      {activeTab === 'billing' && (
        <BillingTab
          usage={usage}
          paymentMethod={paymentMethod}
          showPaymentForm={showPaymentForm}
          setShowPaymentForm={setShowPaymentForm}
          onPaymentAdded={() => { loadData(); setShowPaymentForm(false); showToast('Payment method saved!'); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// ─── Overview Tab ──────────────────────
function OverviewTab({ usage }) {
  if (!usage) return null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="API Calls This Month"
          value={usage.current_month_calls?.toLocaleString() || '0'}
          icon=""
        />
        <StatCard
          label="Free Remaining"
          value={usage.free_remaining?.toLocaleString() || '100'}
          sub={`of ${usage.free_limit || 100} free/month`}
          icon=""
        />
        <StatCard
          label="Estimated Bill"
          value={usage.estimated_bill_dollars || '$0.00'}
          sub={usage.has_payment_method ? 'Payment method on file' : 'No payment method'}
          icon=""
        />
        <StatCard
          label="Active API Keys"
          value={usage.api_key_count || 0}
          sub="Max 5 keys"
          icon=""
        />
      </div>

      <UsageChart data={usage.daily_usage} />

      {/* Pricing Summary */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Your Pricing Tier</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { range: '1 - 100', price: 'FREE', active: (usage.current_month_calls || 0) <= 100 },
            { range: '101 - 10,000', price: '$0.12/call', active: (usage.current_month_calls || 0) > 100 && (usage.current_month_calls || 0) <= 10000 },
            { range: '10,001 - 100,000', price: '$0.10/call', active: (usage.current_month_calls || 0) > 10000 && (usage.current_month_calls || 0) <= 100000 },
            { range: '100,001+', price: '$0.08/call', active: (usage.current_month_calls || 0) > 100000 },
          ].map((tier, i) => (
            <div
              key={i}
              className={`p-4 rounded-lg border ${
                tier.active ? 'border-emerald-500 bg-emerald-500/5' : 'border-slate-700 bg-slate-700/30'
              }`}
            >
              <div className="text-sm text-slate-400">{tier.range} calls</div>
              <div className={`text-lg font-bold mt-1 ${tier.active ? 'text-emerald-400' : 'text-slate-300'}`}>
                {tier.price}
              </div>
              {tier.active && <div className="text-xs text-emerald-400 mt-1">Current tier</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── API Keys Tab ──────────────────────
function KeysTab({ keys, newKey, keyName, setKeyName, creatingKey, createKey, revokeKey, copyToClipboard, setNewKey }) {
  return (
    <div className="space-y-6">
      {/* New Key Reveal */}
      {newKey && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-emerald-400 mb-2">Your New API Key</h3>
          <p className="text-sm text-slate-400 mb-3">
            Copy this key now. It will only be shown once and cannot be retrieved later.
          </p>
          <div className="flex items-center gap-3">
            <code className="flex-1 bg-slate-900 text-green-400 px-4 py-3 rounded-lg text-sm font-mono break-all">
              {newKey}
            </code>
            <button
              onClick={() => copyToClipboard(newKey)}
              className="px-4 py-3 bg-emerald-500 hover:bg-emerald-500/80 text-white rounded-lg text-sm font-medium transition shrink-0"
            >
              Copy
            </button>
          </div>
          <button
            onClick={() => setNewKey(null)}
            className="mt-3 text-sm text-slate-400 hover:text-slate-200 transition"
          >
            I've saved this key
          </button>
        </div>
      )}

      {/* Create Key */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Create API Key</h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            placeholder="Key name (e.g., Production, Testing)"
            className="flex-1 px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition"
          />
          <button
            onClick={createKey}
            disabled={creatingKey}
            className="px-6 py-3 bg-emerald-500 hover:bg-emerald-500/80 disabled:opacity-50 text-white font-medium rounded-lg transition shadow shrink-0"
          >
            {creatingKey ? 'Creating...' : 'Create Key'}
          </button>
        </div>
      </div>

      {/* Keys List */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white">Your API Keys</h3>
        </div>
        {keys.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            No API keys yet. Create one above to get started.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Key</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Last Used</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-400 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {keys.map(key => (
                  <tr key={key.id} className={!key.is_active ? 'opacity-50' : ''}>
                    <td className="px-6 py-4 text-sm text-white font-medium">{key.name}</td>
                    <td className="px-6 py-4">
                      <code className="text-sm text-slate-400 font-mono">{key.prefix}...</code>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-400">
                      {new Date(key.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-400">
                      {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        key.is_active
                          ? 'bg-green-500/10 text-green-400 border border-green-500/30'
                          : 'bg-red-500/10 text-red-400 border border-red-500/30'
                      }`}>
                        {key.is_active ? 'Active' : 'Revoked'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      {key.is_active && (
                        <button
                          onClick={() => revokeKey(key.id)}
                          className="text-sm text-red-400 hover:text-red-300 transition font-medium"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quick Start */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Start</h3>
        <p className="text-slate-400 text-sm mb-3">Include your API key in the <code className="bg-slate-700 px-1.5 py-0.5 rounded text-emerald-400">X-API-Key</code> header:</p>
        <pre className="bg-slate-900 rounded-lg p-4 text-sm overflow-x-auto">
          <code className="text-green-400">{`curl -H "X-API-Key: pr_live_your_key_here" \\
  "https://communityguardapi.com/api/data/search/?lastName=Smith&state=FL"`}</code>
        </pre>
      </div>
    </div>
  );
}

// ─── Billing Tab ──────────────────────
function BillingTab({ usage, paymentMethod, showPaymentForm, setShowPaymentForm, onPaymentAdded, showToast }) {
  const [removingPM, setRemovingPM] = useState(false);

  const removePaymentMethod = async () => {
    setRemovingPM(true);
    try {
      await api.billing.removePaymentMethod();
      showToast('Payment method removed.');
      onPaymentAdded(); // Refresh data
    } catch (err) {
      showToast('Failed to remove payment method.', 'error');
    }
    setRemovingPM(false);
  };

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Current Plan</h3>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xl font-bold text-emerald-400">Pay-As-You-Go</div>
            <div className="text-slate-400 text-sm mt-1">100 free calls/month, then tiered pricing</div>
          </div>
          <Link to="/pricing" className="text-emerald-400 hover:text-amber-400 text-sm font-medium transition">
            View pricing details
          </Link>
        </div>
      </div>

      {/* Payment Method */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Payment Method</h3>
        {paymentMethod?.has_payment_method ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-slate-700 rounded-lg p-3">
                <svg className="w-8 h-5 text-slate-300" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="1" y="4" width="22" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="2"/>
                  <line x1="1" y1="10" x2="23" y2="10" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div>
                <div className="text-white font-medium capitalize">
                  {paymentMethod.brand} ending in {paymentMethod.last4}
                </div>
                <div className="text-slate-400 text-sm">Default payment method</div>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPaymentForm(true)}
                className="text-sm text-emerald-400 hover:text-amber-400 transition font-medium"
              >
                Update
              </button>
              <button
                onClick={removePaymentMethod}
                disabled={removingPM}
                className="text-sm text-red-400 hover:text-red-300 transition font-medium"
              >
                {removingPM ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-slate-400 text-sm mb-4">
              No payment method on file. Add one to continue using the API beyond the free tier (100 calls/month).
            </p>
            <button
              onClick={() => setShowPaymentForm(true)}
              className="px-6 py-3 bg-emerald-500 hover:bg-emerald-500/80 text-white font-medium rounded-lg transition shadow"
            >
              Add Payment Method
            </button>
          </div>
        )}

        {showPaymentForm && (
          <PaymentForm
            onSuccess={onPaymentAdded}
            onCancel={() => setShowPaymentForm(false)}
          />
        )}
      </div>

      {/* Usage This Month */}
      {usage && usage.current_month_calls > 0 && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Current Month Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <div className="text-slate-400 text-sm">Total Calls</div>
              <div className="text-xl font-bold text-white">{usage.current_month_calls?.toLocaleString()}</div>
            </div>
            <div>
              <div className="text-slate-400 text-sm">Free Calls Used</div>
              <div className="text-xl font-bold text-white">{Math.min(usage.current_month_calls || 0, usage.free_limit || 100)}</div>
            </div>
            <div>
              <div className="text-slate-400 text-sm">Billed Calls</div>
              <div className="text-xl font-bold text-white">{Math.max(0, (usage.current_month_calls || 0) - (usage.free_limit || 100)).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-slate-400 text-sm">Estimated Bill</div>
              <div className="text-xl font-bold text-amber-400">{usage.estimated_bill_dollars || '$0.00'}</div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice History - placeholder for when invoices exist */}
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Invoice History</h3>
        <p className="text-slate-500 text-center py-4">
          No invoices yet. Invoices are generated monthly for usage above the free tier.
        </p>
      </div>
    </div>
  );
}

// ─── Payment Form (Stripe via script tag) ──────────────────────
function PaymentForm({ onSuccess, onCancel }) {
  const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  const [stripe, setStripe] = useState(null);
  const [cardElement, setCardElement] = useState(null);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [ready, setReady] = useState(false);
  const cardRef = React.useRef(null);

  useEffect(() => {
    if (!stripeKey) return;

    // Load Stripe.js via script tag to avoid bundler resolution issues
    if (window.Stripe) {
      initStripe(window.Stripe);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;
    script.onload = () => {
      if (window.Stripe) initStripe(window.Stripe);
    };
    document.head.appendChild(script);

    return () => {
      if (cardElement) {
        try { cardElement.destroy(); } catch (e) { /* ignore */ }
      }
    };
  }, [stripeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const initStripe = (StripeConstructor) => {
    const stripeInstance = StripeConstructor(stripeKey);
    setStripe(stripeInstance);
    const elements = stripeInstance.elements();
    const card = elements.create('card', {
      style: {
        base: { color: '#fff', fontSize: '16px', '::placeholder': { color: '#64748b' } },
        invalid: { color: '#fca5a5' },
      },
    });
    setCardElement(card);

    // Mount after a tick so the DOM node exists
    setTimeout(() => {
      if (cardRef.current) {
        card.mount(cardRef.current);
        setReady(true);
      }
    }, 50);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !cardElement) return;

    setProcessing(true);
    setError('');

    try {
      const { data } = await api.billing.createSetupIntent();

      const result = await stripe.confirmCardSetup(data.client_secret, {
        payment_method: { card: cardElement },
      });

      if (result.error) {
        setError(result.error.message);
        setProcessing(false);
      } else {
        await api.billing.addPaymentMethod(result.setupIntent.payment_method);
        setProcessing(false);
        onSuccess();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Payment setup failed.');
      setProcessing(false);
    }
  };

  if (!stripeKey) {
    return (
      <div className="mt-4 p-4 border border-slate-600 rounded-lg">
        <p className="text-slate-400 text-sm mb-3">
          Stripe payment processing is being set up. Please check back soon or contact support at{' '}
          <a href="mailto:support@communityguardapi.com" className="text-emerald-400">support@communityguardapi.com</a>
        </p>
        <button onClick={onCancel} className="text-sm text-slate-400 hover:text-slate-200 transition">
          Cancel
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4 p-4 border border-slate-600 rounded-lg space-y-4">
      <div ref={cardRef} className="bg-slate-700/50 rounded-lg p-4 min-h-[44px]">
        {!ready && <span className="text-slate-500 text-sm">Loading card form...</span>}
      </div>
      {error && <p className="text-red-400 text-sm">{error}</p>}
      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!ready || processing}
          className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-500/80 disabled:opacity-50 text-white font-medium rounded-lg transition"
        >
          {processing ? 'Saving...' : 'Save Card'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2.5 border border-slate-600 text-slate-300 hover:text-white rounded-lg transition"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── API Playground Tab ──────────────────────
function PlaygroundTab({ keys }) {
  const [endpoint, setEndpoint] = useState('search');
  const [params, setParams] = useState({});
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [responseTime, setResponseTime] = useState(null);

  const activeKey = keys.find(k => k.is_active);

  const ENDPOINTS = {
    search: {
      label: 'Search Offenders',
      path: '/api/data/search/',
      fields: [
        { name: 'lastName', label: 'Last Name', placeholder: 'Smith' },
        { name: 'firstName', label: 'First Name', placeholder: 'John' },
        { name: 'state', label: 'State', placeholder: 'FL' },
        { name: 'zipCode', label: 'ZIP Code', placeholder: '33101' },
        { name: 'page_size', label: 'Page Size', placeholder: '5', default: '5' },
      ],
    },
    nearby: {
      label: 'Nearby Offenders',
      path: '/api/data/offenders/nearby/',
      fields: [
        { name: 'latitude', label: 'Latitude', placeholder: '25.7617' },
        { name: 'longitude', label: 'Longitude', placeholder: '-80.1918' },
        { name: 'radius', label: 'Radius (miles)', placeholder: '2' },
      ],
    },
    detail: {
      label: 'Offender Detail',
      path: '/api/data/offenders/{id}/',
      fields: [
        { name: 'id', label: 'Offender ID', placeholder: '12345' },
      ],
    },
    stats: {
      label: 'Statistics',
      path: '/api/data/stats/',
      fields: [
        { name: 'state', label: 'State (optional)', placeholder: 'FL' },
      ],
    },
  };

  const currentEndpoint = ENDPOINTS[endpoint];

  const updateParam = (name, value) => {
    setParams(prev => ({ ...prev, [name]: value }));
  };

  const buildUrl = () => {
    let path = currentEndpoint.path;
    const queryParams = {};

    currentEndpoint.fields.forEach(f => {
      const val = params[f.name] || f.default || '';
      if (!val) return;
      if (path.includes(`{${f.name}}`)) {
        path = path.replace(`{${f.name}}`, val);
      } else {
        queryParams[f.name] = val;
      }
    });

    const qs = Object.entries(queryParams)
      .filter(([, v]) => v)
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
      .join('&');

    return `https://communityguardapi.com${path}${qs ? '?' + qs : ''}`;
  };

  const runQuery = async () => {
    setLoading(true);
    setError('');
    setResponse(null);
    const start = performance.now();

    try {
      let path = currentEndpoint.path;
      const queryParams = {};

      currentEndpoint.fields.forEach(f => {
        const val = params[f.name] || f.default || '';
        if (!val) return;
        if (path.includes(`{${f.name}}`)) {
          path = path.replace(`{${f.name}}`, val);
        } else {
          queryParams[f.name] = val;
        }
      });

      let result;
      if (endpoint === 'search') {
        result = await api.offenders.search(queryParams);
      } else if (endpoint === 'nearby') {
        result = await api.offenders.nearby(
          queryParams.latitude, queryParams.longitude, queryParams.radius
        );
      } else if (endpoint === 'detail') {
        result = await api.offenders.getById(params.id);
      } else if (endpoint === 'stats') {
        result = await api.stats.get(queryParams);
      }

      setResponseTime(Math.round(performance.now() - start));
      setResponse(result?.data);
    } catch (err) {
      setResponseTime(Math.round(performance.now() - start));
      setError(err.response?.data?.error || err.message || 'Request failed');
    }
    setLoading(false);
  };

  const curlCommand = `curl -H "X-API-Key: ${activeKey ? activeKey.prefix + '...' : 'YOUR_KEY'}" \\\n  "${buildUrl()}"`;

  return (
    <div className="space-y-6">
      <div className="bg-slate-800 border border-slate-700 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-white mb-1">API Playground</h3>
        <p className="text-slate-400 text-sm mb-5">Test API endpoints live with your account credentials.</p>

        {/* Endpoint picker */}
        <div className="flex flex-wrap gap-2 mb-5">
          {Object.entries(ENDPOINTS).map(([key, ep]) => (
            <button
              key={key}
              onClick={() => { setEndpoint(key); setParams({}); setResponse(null); setError(''); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                endpoint === key
                  ? 'bg-emerald-500 text-white'
                  : 'bg-slate-700/60 text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
            >
              {ep.label}
            </button>
          ))}
        </div>

        {/* Method + Path */}
        <div className="flex items-center gap-3 mb-5 bg-slate-900 rounded-lg p-3 border border-slate-700">
          <span className="px-2.5 py-1 text-xs font-bold rounded bg-green-500/20 text-green-400 border border-green-500/30">
            GET
          </span>
          <code className="text-emerald-400 font-mono text-sm">{currentEndpoint.path}</code>
        </div>

        {/* Parameter fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
          {currentEndpoint.fields.map(f => (
            <div key={f.name}>
              <label className="block text-xs font-medium text-slate-400 mb-1">{f.label}</label>
              <input
                type="text"
                value={params[f.name] || ''}
                onChange={(e) => updateParam(f.name, e.target.value)}
                placeholder={f.placeholder}
                className="w-full px-3 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-emerald-500 transition"
              />
            </div>
          ))}
        </div>

        <button
          onClick={runQuery}
          disabled={loading}
          className="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-semibold rounded-lg transition shadow-lg"
        >
          {loading ? 'Running...' : 'Send Request'}
        </button>
      </div>

      {/* cURL command */}
      <div className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800/50 border-b border-slate-700">
          <span className="text-xs font-mono text-slate-500">cURL</span>
          <button
            onClick={() => navigator.clipboard?.writeText(curlCommand)}
            className="text-xs text-slate-500 hover:text-emerald-400 transition"
          >
            Copy
          </button>
        </div>
        <pre className="p-4 text-xs sm:text-sm overflow-x-auto">
          <code className="text-slate-300">{curlCommand}</code>
        </pre>
      </div>

      {/* Response */}
      {(response || error) && (
        <div className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800/50 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <span className="text-xs font-mono text-slate-500">Response</span>
              {responseTime != null && (
                <span className={`text-xs font-mono ${responseTime < 500 ? 'text-green-400' : responseTime < 2000 ? 'text-yellow-400' : 'text-red-400'}`}>
                  {responseTime}ms
                </span>
              )}
            </div>
            {error && <span className="text-xs text-red-400 font-semibold">Error</span>}
            {response && <span className="text-xs text-green-400 font-semibold">200 OK</span>}
          </div>
          <pre className="p-4 text-xs sm:text-sm overflow-x-auto max-h-96 overflow-y-auto">
            <code className={error ? 'text-red-300' : 'text-green-300'}>
              {error || JSON.stringify(response, null, 2)}
            </code>
          </pre>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
